/**
 * @file email-language-constants.ts
 *
 * Locale options for the shop's outgoing-email language setting, ported verbatim
 * from WSB-v2's Shop settings. Each entry maps a WordPress/WooCommerce locale
 * code (`value`) to its native language name (`label`), mirroring the official
 * WordPress locale registry so the value can be sent to the API without any
 * translation step. Kept separate from `settings-constants.ts` because of its size.
 */

import type { SettingsOption } from './settings-form-types';

/** Outgoing-email language options (WP locale code → native name). */
export const EMAIL_LANGUAGE_OPTIONS: SettingsOption[] = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'ar', label: 'العربية' },
  { value: 'ary', label: 'العربية المغربية' },
  { value: 'as', label: 'অসমীয়া' },
  { value: 'az', label: 'Azərbaycan dili' },
  { value: 'azb', label: 'گؤنئی آذربایجان' },
  { value: 'bel', label: 'Беларуская мова' },
  { value: 'bg_BG', label: 'Български' },
  { value: 'bn_BD', label: 'বাংলা' },
  { value: 'bn_IN', label: 'বাংলা' },
  { value: 'bo', label: 'བོད་ཡིག' },
  { value: 'bs_BA', label: 'Bosanski' },
  { value: 'ca', label: 'Català' },
  { value: 'ceb', label: 'Cebuano' },
  { value: 'ckb', label: 'كوردی‎' },
  { value: 'cs_CZ', label: 'Čeština' },
  { value: 'cy', label: 'Cymraeg' },
  { value: 'da_DK', label: 'Dansk' },
  { value: 'de_AT', label: 'Deutsch (Österreich)' },
  { value: 'de_CH', label: 'Deutsch (Schweiz)' },
  { value: 'de_CH_informal', label: 'Deutsch (Schweiz, Du)' },
  { value: 'de_DE', label: 'Deutsch' },
  { value: 'de_DE_formal', label: 'Deutsch (Sie)' },
  { value: 'dzo', label: 'རྫོང་ཁ' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'en_AU', label: 'English (Australia)' },
  { value: 'en_CA', label: 'English (Canada)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_NZ', label: 'English (New Zealand)' },
  { value: 'en_US', label: 'English (United States)' },
  { value: 'en_ZA', label: 'English (South Africa)' },
  { value: 'eo', label: 'Esperanto' },
  { value: 'es_AR', label: 'Español de Argentina' },
  { value: 'es_CL', label: 'Español de Chile' },
  { value: 'es_CO', label: 'Español de Colombia' },
  { value: 'es_CR', label: 'Español de Costa Rica' },
  { value: 'es_ES', label: 'Español' },
  { value: 'es_GT', label: 'Español de Guatemala' },
  { value: 'es_MX', label: 'Español de México' },
  { value: 'es_PE', label: 'Español de Perú' },
  { value: 'es_UY', label: 'Español de Uruguay' },
  { value: 'es_VE', label: 'Español de Venezuela' },
  { value: 'et', label: 'Eesti' },
  { value: 'eu', label: 'Euskara' },
  { value: 'fa_IR', label: 'فارسی' },
  { value: 'fi', label: 'Suomi' },
  { value: 'fr_BE', label: 'Français de Belgique' },
  { value: 'fr_CA', label: 'Français du Canada' },
  { value: 'fr_FR', label: 'Français' },
  { value: 'fur', label: 'Friulian' },
  { value: 'gd', label: 'Gàidhlig' },
  { value: 'gl_ES', label: 'Galego' },
  { value: 'gu', label: 'ગુજરાતી' },
  { value: 'haz', label: 'هزاره گی' },
  { value: 'he_IL', label: 'עִבְרִית' },
  { value: 'hi_IN', label: 'हिन्दी' },
  { value: 'hr', label: 'Hrvatski' },
  { value: 'hsb', label: 'Hornjoserbšćina' },
  { value: 'hu_HU', label: 'Magyar' },
  { value: 'hy', label: 'Հայերեն' },
  { value: 'id_ID', label: 'Bahasa Indonesia' },
  { value: 'is_IS', label: 'Íslenska' },
  { value: 'it_IT', label: 'Italiano' },
  { value: 'ja', label: '日本語' },
  { value: 'jv_ID', label: 'Basa Jawa' },
  { value: 'kab', label: 'Taqbaylit' },
  { value: 'ka_GE', label: 'ქართული' },
  { value: 'kk', label: 'Қазақ тілі' },
  { value: 'km', label: 'ភាសាខ្មែរ' },
  { value: 'kn', label: 'ಕನ್ನಡ' },
  { value: 'ko_KR', label: '한국어' },
  { value: 'lo', label: 'ພາສາລາວ' },
  { value: 'lt_LT', label: 'Lietuvių kalba' },
  { value: 'lv', label: 'Latviešu valoda' },
  { value: 'mk_MK', label: 'Македонски јазик' },
  { value: 'ml_IN', label: 'മലയാളം' },
  { value: 'mn', label: 'Монгол' },
  { value: 'mr', label: 'मराठी' },
  { value: 'ms_MY', label: 'Bahasa Melayu' },
  { value: 'my_MM', label: 'ဗမာစာ' },
  { value: 'nb_NO', label: 'Norsk bokmål' },
  { value: 'ne_NP', label: 'नेपाली' },
  { value: 'nl_BE', label: 'Nederlands (België)' },
  { value: 'nl_NL', label: 'Nederlands' },
  { value: 'nl_NL_formal', label: 'Nederlands (Formeel)' },
  { value: 'nn_NO', label: 'Norsk nynorsk' },
  { value: 'oci', label: 'Occitan' },
  { value: 'pa_IN', label: 'ਪੰਜਾਬੀ' },
  { value: 'pl_PL', label: 'Polski' },
  { value: 'ps', label: 'پښتو' },
  { value: 'pt_AO', label: 'Português de Angola' },
  { value: 'pt_BR', label: 'Português do Brasil' },
  { value: 'pt_PT', label: 'Português' },
  { value: 'pt_PT_ao90', label: 'Português (AO90)' },
  { value: 'rhg', label: 'Ruáinga' },
  { value: 'ro_RO', label: 'Română' },
  { value: 'ru_RU', label: 'Русский' },
  { value: 'sah', label: 'Сахалыы' },
  { value: 'si_LK', label: 'සිංහල' },
  { value: 'skr', label: 'سرائیکی' },
  { value: 'sk_SK', label: 'Slovenčina' },
  { value: 'sl_SI', label: 'Slovenščina' },
  { value: 'snd', label: 'سنڌي' },
  { value: 'sq', label: 'Shqip' },
  { value: 'sr_RS', label: 'Српски језик' },
  { value: 'sv_SE', label: 'Svenska' },
  { value: 'sw', label: 'Kiswahili' },
  { value: 'szl', label: 'Ślōnskŏ gŏdka' },
  { value: 'tah', label: 'Reo Tahiti' },
  { value: 'ta_IN', label: 'தமிழ்' },
  { value: 'te', label: 'తెలుగు' },
  { value: 'th', label: 'ไทย' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'tr_TR', label: 'Türkçe' },
  { value: 'tt_RU', label: 'Татар теле' },
  { value: 'ug_CN', label: 'ئۇيغۇرچە' },
  { value: 'uk', label: 'Українська' },
  { value: 'ur', label: 'اردو' },
  { value: 'uz_UZ', label: "O'zbekcha" },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'zh_CN', label: '简体中文' },
  { value: 'zh_HK', label: '香港中文版' },
  { value: 'zh_TW', label: '繁體中文' },
];
